import React from 'react'
import { captureException } from '@sentry/browser'
import { DateTime } from 'luxon'
import { isEmpty, has, groupBy, cloneDeep } from 'lodash-es'
import SVG from 'react-inlinesvg'
import {
  postEvent,
  deleteItem,
  getCart,
  postItemsToCart,
  getRemainingTickets,
  updateReferrerItems,
  postNotes,
  putPreferredRecognition,
  removePromoCode
} from '../../services/api'
import params from '../../services/params'
import storage from '../../services/storage'
import { CartIcon, Contact } from '../../shared/components'
import {
  OrderSummary,
  OrderActions,
  EventItemContainer,
  MembershipItem
} from '../components'
import { Loading, CartExpiration } from '../../shared/components'
import { CartsAppContext } from '../context/carts-app-context'
import ticketIcon from '../../../../assets/images/ticket.svg' // in app/assets
import {
  isMembershipType,
  clearTicketsAppSessionData,
  hasMembershipDependentItems,
  membershipInCart,
  needsToUpdateTickets,
  eventsGroupedByDateWithDependency,
  sortedEventsGroupedByDate,
  notMembershipType,
  findDependenciesFor
} from '../helpers/logic'
import DateFormat from '../../shared/helpers/date-format'
import { ticketsChanged } from '../../shared/helpers/logic'
import { ticketsWithConstituencyPricing } from '../../shared/helpers/logic'
import { errorMessages } from '../../shared/helpers/error-messages'
import { includesEmployee } from '../../tickets/helpers/logic'

const { fancyFull, dashed } = DateFormat

const TEST_CURRENT_DATE = storage.getItem('testCurrentDate')

const fetchRemainingTickets = items => {
  const eventItems = items.filter(item => !isMembershipType(item))

  return Promise.all(
    eventItems.map(item => {
      const ticketsWithConstituencyPricingIds = ticketsWithConstituencyPricing(
        item.tickets
      ).map(t => t.priceTypeId)

      return ticketsWithConstituencyPricingIds.length > 0
        ? getRemainingTickets(
            item.externalId,
            item.nativeEventId,
            ticketsWithConstituencyPricingIds
          )
        : getRemainingTickets(item.externalId)
    })
  )
}

const getMemberPricingTicketsExist = items => {
  const eventItems = items.filter(item => !isMembershipType(item))
  return Promise.all(eventItems.map(item => has(item.pricing, 'member')))
}

const cantContinueConstTickets = items => {
  const eventItems = items.filter(item => !isMembershipType(item))
  let hasEmployeeTickets = false
  let hasConstituencyTickets = false
  let constituencyTickets = []
  eventItems.forEach(item => {
    constituencyTickets.push(ticketsWithConstituencyPricing(item.tickets))
    if (constituencyTickets.flat().length > 0) {
      hasConstituencyTickets = true
    }
    if (includesEmployee(item.tickets)) hasEmployeeTickets = true
  })
  constituencyTickets = constituencyTickets.flat()
  return (
    constituencyTickets.length > 1 ||
    (hasEmployeeTickets && hasConstituencyTickets)
  )
}

const itemsHaveRemainingTickets = async items => {
  const itemsRemainingTickets = await fetchRemainingTickets(items)

  const anyItemsHaveRemainingTickets = itemsRemainingTickets.some(
    remainingTickets => remainingTickets.remainingMembershipTickets > 0
  )

  let dependencyRemainingTickets = 0
  const dependencies = findDependenciesFor(items)

  const dependenceisIds = dependencies.map(item => item.externalId)
  dependenceisIds.forEach(dependId => {
    itemsRemainingTickets.forEach(item => {
      if (+item.performanceId == dependId) {
        dependencyRemainingTickets += +item.remainingMembershipTickets
        dependencyRemainingTickets += +item.remainingEmployeeTickets
      }
    })
  })

  return anyItemsHaveRemainingTickets && dependencyRemainingTickets > 0
}

export default class Cart extends React.Component {
  initialize = async () => {
    clearTicketsAppSessionData()
    let updateNeeded = false
    let cart = null
    let cleared = params.get('cleared') === 'true'
    // Using an integer instead of a boolean because you can click on multiple remove buttons at once.
    let inProgressUpdateCount = 0
    try {
      cart = await getCart()
      this.originalCartEventItems = cloneDeep(
        cart.items.filter(notMembershipType)
      )
      const cartItems = cart.items

      const memberPricingTicketsExist =
        await getMemberPricingTicketsExist(cartItems)

      const anyItemsHaveRemainingTickets =
        await itemsHaveRemainingTickets(cartItems)

      if (
        needsToUpdateTickets(cartItems, cart.membership) &&
        memberPricingTicketsExist.includes(true) &&
        anyItemsHaveRemainingTickets
      ) {
        updateNeeded = true
      }

      this.setState({
        cart: { ...cart, cleared },
        updateNeeded,
        loading: false,
        error: null,
        inProgressUpdateCount: inProgressUpdateCount
      })
    } catch (e) {
      console.warn(e)
      if (process.env.NODE_ENV === 'production') {
        captureException(e)
      }
      const errorMessage = updateNeeded
        ? "There's been an error updating your non-member tickets, please use the links below."
        : 'There was an error fetching your cart'
      let error = new Error(errorMessage)
      error.code = 'client'
      this.setError(error, {
        cart,
        updateNeeded
      })
    }
  }

  abortController = new AbortController()

  setError = (error, stateChanges = {}) => {
    // if we experience an error we
    // warn
    // set errors, and any state updates
    const showError =
      error.code && ['api', 'client', 'contact'].includes(error.code)
    let baseError = new Error('We are having issues with your cart.')
    baseError.code = 'contact'
    console.warn(error)
    this.setState(prevState => {
      const changes =
        typeof stateChanges === 'function' ? stateChanges(prevState) : stateChanges
      return {
        ...changes,
        error: showError ? error : baseError,
        loading: false
      }
    })

  }

  renderError = (error, messages) => {
    if (error) {
      let errorElement = <p className="font-bold text-16">{error.message}</p>
      if (error && error.code === 'contact') {
        errorElement = (
          <Contact
            preText="We are having trouble with this event."
            email="ContactUs@sheddaquarium.org"
            phone="312-939-2438"
          />
        )
      }
      return errorElement
    } else if (messages && messages.length > 0) {
      return <p className="font-bold text-16">{messages.join(', ')}</p>
    } else {
      return null
    }
  }

  updateCartItem = cartItem => {
    this.setState(currentState => {
      const {
        cart: { items }
      } = currentState
      let newCartItems = items.map(item => {
        if (item.id !== cartItem.id) {
          return item
        }
        return {
          ...item,
          ...cartItem
        }
      })

      return {
        cart: {
          ...currentState.cart,
          items: newCartItems
        }
      }
    })
  }

  deleteCartItemTicket = async (item, ticket) => {
    this.setState(prevState => ({
      inProgressUpdateCount: prevState.inProgressUpdateCount + 1
    }))
    const { id: cartId } = this.state.cart
    let newItem = {
      ...item,
      tickets: item.tickets.filter(
        itemTicket => itemTicket.priceTypeId !== ticket.priceTypeId
      )
    }
    let cart = null
    let updateNeeded = false
    try {
      if (newItem.tickets.length == 0) {
        cart = await deleteItem(newItem.id)
        this.removeNote(newItem.externalId)
      } else {
        cart = await postEvent(
          newItem,
          cartId,
          'We are having trouble removing that ticket from your cart.'
        )
      }

      const anyItemsHaveRemainingTickets = await itemsHaveRemainingTickets(
        cart.items
      )

      if (
        needsToUpdateTickets(cart.items, cart.membership) &&
        anyItemsHaveRemainingTickets
      ) {
        updateNeeded = true
      }

      this.setState(prevState => ({
        cart: { ...cart, notes: prevState.cart.notes },
        updateNeeded,
        error: null,
        inProgressUpdateCount: prevState.inProgressUpdateCount - 1
      }))
    } catch (e) {
      this.setError(e, prevState => ({
        inProgressUpdateCount: prevState.inProgressUpdateCount - 1
      }))
    } finally {
      if (this.cartEmpty()) {
        this.removeTicketPromoCode()
      }
    }
  }

  deleteCartItem = async itemId => {
    try {
      this.setState(prevState => ({
        inProgressUpdateCount: prevState.inProgressUpdateCount + 1
      }))
      const cart = await deleteItem(itemId)
      let updateNeeded = false
      const anyItemsHaveRemainingTickets = await itemsHaveRemainingTickets(
        cart.items
      )
      if (
        needsToUpdateTickets(cart.items, cart.membership) &&
        anyItemsHaveRemainingTickets
      ) {
        updateNeeded = true
      }

      this.setState(prevState => ({
        cart: { ...cart, notes: prevState.cart.notes },
        updateNeeded,
        error: null,
        inProgressUpdateCount: prevState.inProgressUpdateCount - 1
      }))
    } catch (e) {
      this.setError(e, prevState => ({
        inProgressUpdateCount: prevState.inProgressUpdateCount - 1
      }))
    }
  }

  // SHEDDWEB-542 Deleting notes from the state if there are any
  removeNote = itemExternalId => {
    let notes = this.state.cart.notes
    if (Object.keys(notes).length > 0) {
      delete notes[itemExternalId]
      this.setState(prevState => ({
        cart: { ...prevState.cart, notes: notes }
      }))
    }
  }

  saveCart = async () => {
    try {
      await this.addItemsToCart()
      location.assign(this.props.checkoutPath)
    } catch (e) {
      this.setError(e)
    }
  }

  saveNotes = async () => {
    const { notes } = this.state.cart
    if (isEmpty(notes)) return
    const ids = Object.keys(notes)
    const data = ids.map(id => ({ performanceId: id, text: notes[id] }))

    try {
      await postNotes(data)
    } catch (e) {
      this.setError(e)
    }
  }

  saveReferrers = async () => {
    let referrerInfo = []
    this.state.cart.items.forEach(item => {
      if (item.showReferredBy && item.referrerName) {
        referrerInfo.push({
          itemId: item.id,
          referrerName: item.referrerName
        })
      }
    })
    if (isEmpty(referrerInfo)) return
    try {
      updateReferrerItems(this.abortController.signal, referrerInfo)
    } catch (e) {
      this.setError(e)
    }
  }

  saveRecognitionTexts = async () => {
    let data = {}
    this.state.cart.items.forEach(item => {
      if (
        item.type === 'membership_plan' &&
        item.showPreferredRecognitionName
      ) {
        data['preferred_recognition_name'] =
          item.preferredRecognitionName === null
            ? 'Anonymous'
            : item.preferredRecognitionName
        if (item.autoRenew) {
          data['auto_renew'] = item.autoRenew
        }
      }
    })

    if (isEmpty(data)) return

    try {
      await putPreferredRecognition(this.abortController.signal, data)
    } catch (e) {
      this.setError(e)
    }
  }

  setCart = async cart => {
    this.setState({ cart })
  }

  adjustTickets = (date, productId) => async _ => {
    const { items, expiresAt, cleared } = this.state.cart
    let cartExpiration = expiresAt
    if (typeof cartExpiration === 'string') {
      cartExpiration = new Date(expiresAt).getTime()
    }
    const dateTimeNow = DateTime.fromMillis(Date.now()).setZone(
      'America/Chicago'
    )
    const dateTimeFromNow =
      DateTime.fromMillis(cartExpiration).setZone('America/Chicago')
    const difference = dateTimeFromNow.diff(dateTimeNow)
    const chicagoDate = DateTime.fromISO(date, { zone: 'America/Chicago' })
    if ((difference <= 0 || cleared) && !TEST_CURRENT_DATE) {
      return location.reload()
    } else {
      const cartItem = items.find(item => item.nativeEventId === productId)
      if (cartItem) {
        location.assign(
          `/tickets?event_id=${productId}&date=${dashed(
            chicagoDate
          )}&external_id=${cartItem.externalId}`
        )
      } else {
        let error = new Error('Cannot find item in cart')
        error.code = 'client'
        this.setError(error)
      }
    }
  }

  renderCartEvents = () => {
    const cartItems = this.state.cart.items
    const cartHasMembership = membershipInCart(cartItems)
    const activeMember = this.state.cart.membership.active
    const eventItems = cartItems.filter(notMembershipType)
    const eventItemsGroupedByDate = groupBy(eventItems, item =>
      fancyFull(item.occursAt)
    )
    const eventsGroupedByDate = eventsGroupedByDateWithDependency(
      eventItemsGroupedByDate
    )

    const orderedByDateAsc = eventsGroupedByDate.sort((a, b) => {
      if (a.items[0].occursAt > b.items[0].occursAt) {
        return 1
      } else if (a.items[0].occursAt < b.items[0].occursAt) {
        return -1
      } else {
        return 0
      }
    })

    const sortedEvents = sortedEventsGroupedByDate(
      orderedByDateAsc,
      this.state.updateNeeded
    )

    return sortedEvents.map(({ date, dependencies, items }, index) => {
      return (
        <EventItemContainer
          key={index}
          date={date}
          dependencies={dependencies}
          items={items}
          activeMember={activeMember}
          adjustTickets={this.adjustTickets}
          cartHasMembership={cartHasMembership}
          updateNeeded={this.state.updateNeeded}
          membership={this.state.cart.membership}
        />
      )
    })
  }

  renderCartMemberships = () => {
    const cartItems = this.state.cart.items
    const membershipItems = cartItems.filter(isMembershipType)
    const cannotRemove = hasMembershipDependentItems(cartItems)
    return membershipItems.map(item => (
      <MembershipItem
        key={item.id}
        item={item}
        cannotRemove={cannotRemove}
      />
    ))
  }

  cartEmpty = () => {
    const { cart } = this.state
    return cart.items.length === 0
  }

  removeTicketPromoCode = async () => {
    const {
      cart: { promotionalCodeValue }
    } = this.state
    if (promotionalCodeValue !== null) {
      try {
        await removePromoCode(false)
      } catch (e) {
        this.setError(e)
      }
    }
  }

  addItemsToCart = async () => {
    const {
      cart: { items, id }
    } = this.state
    const eventItems = items.filter(notMembershipType)
    let eventItemsInNeedOfUpdate = eventItems.filter(item => {
      let originalItem = this.originalCartEventItems.find(
        ogItem => item.externalId === ogItem.externalId
      )
      return originalItem && ticketsChanged(originalItem.tickets, item.tickets)
    })
    try {
      await postItemsToCart(eventItemsInNeedOfUpdate, id)
    } catch (e) {
      this.setError(e)
    }
  }

  // called before the page unloads, to handle tasks/cleanup when the user navigates
  // away from the cart page. you can await asynchronous actions but be careful
  // not to make the user wait too long for requests to finish.
  handlePageUnload = async () => {
    await this.saveNotes()
    await this.saveReferrers()
    if (this.state.cart.items.some(i => i.type === 'membership_plan')) {
      await this.saveRecognitionTexts()
    }
  }

  state = {
    error: null,
    cart: {
      items: []
    },
    loading: true,
    checkoutPath: this.props.checkoutPath,
    actions: {
      updateCartItem: this.updateCartItem,
      saveCart: this.saveCart,
      deleteCartItemTicket: this.deleteCartItemTicket,
      deleteCartItem: this.deleteCartItem,
      setCart: this.setCart
    }
  }

  // componentDidMount is called ONCE when the React component is successfully
  // mounted to the DOM for the first time
  componentDidMount() {
    window.addEventListener('beforeunload', this.handlePageUnload)
    return this.initialize()
  }

  // componentWillUnmount is called ONCE before the React component removes itself
  // from the DOM. not asynchronous, so only do synchronous cleanup here.
  componentWillUnmount() {
    this.abortController.abort()
    window.removeEventListener('beforeunload', this.handlePageUnload)
  }

  render() {
    const {
      loading,
      error,
      updateNeeded,
      cart: { items, cleared, expiresAt, cartMessages }
    } = this.state

    let cantContinueConstituencyTickets = cantContinueConstTickets(items)

    return (
      <React.Fragment>
        <CartIcon items={items} />

        <CartsAppContext.Provider value={this.state}>
          <React.Fragment>
            <div className="ecomm-container">
              <div className="ecomm-main">
                <div className="ecomm-main__inner">
                  {this.renderError(error, cartMessages)}
                  {cantContinueConstituencyTickets && (
                    <p className="font-bold text-16">
                      {errorMessages.constituencyTicketsCantContinue}
                    </p>
                  )}
                  {updateNeeded && !cleared && (
                    <React.Fragment>
                      <p className="font-bold text-center">
                        Member discounts available
                      </p>
                      <p className="text-center">
                        You have member pricing available for products in your
                        cart. Please update your tickets before proceeding to
                        checkout.
                      </p>
                    </React.Fragment>
                  )}
                  {cleared && (
                    <p className="font-bold text-red text-center">
                      We're sorry, your transaction has timed out. Please try
                      again.
                    </p>
                  )}
                  {loading && <Loading />}
                  {!loading && (
                    <React.Fragment>
                      <div className="page-header-container divider-2-green">
                        <h1>Your Cart</h1>
                      </div>

                      {!this.cartEmpty() && (
                        <React.Fragment>
                          {this.renderCartEvents()}
                          {this.renderCartMemberships()}
                          <div className="bordered-content md:hidden">
                            <OrderSummary />
                            <OrderActions />
                          </div>
                          <p>
                            If you have a promo code or gift certificate to
                            apply towards this purchase, you may do so during
                            checkout.
                          </p>
                        </React.Fragment>
                      )}

                      {this.cartEmpty() && (
                        <React.Fragment>
                          <p>There are no items in your cart.</p>
                          <div className="sm:mt-40 sm:flex">
                            <a
                              href="/tickets"
                              className="button -big my-12 inline-flex sm:flex-no-grow sm:flex-basis-320 sm:flex-shrink sm:mr-12"
                            >
                              <SVG
                                aria-hidden="true"
                                aria-label="ticket"
                                focusable="false"
                                src={ticketIcon}
                                width="16"
                                height="16"
                                className="mr-8"
                              />
                              <span>Get Tickets</span>
                            </a>
                            <a
                              href="/calendar"
                              className="button -big -depths my-12 sm:flex-no-grow sm:flex-basis-320 sm:flex-shrink sm:ml-12 x-whitespace-nowrap"
                            >
                              View Calendar
                            </a>
                          </div>
                          <div>
                            <a
                              href="/plan-a-visit/group-visits"
                              className="text-link-small"
                            >
                              Buying tickets for more than 14 people? Plan your
                              group visit today!
                            </a>
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </div>
              </div>

              <div className="ecomm-sidebar">
                <div className="ecomm-sidebar__inner">
                  {!this.cartEmpty() && (
                    <React.Fragment>
                      <CartExpiration expiresAt={expiresAt} />
                      <div className="hidden md:block">
                        <OrderSummary />
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        </CartsAppContext.Provider>
      </React.Fragment>
    )
  }
}
